import React, { useEffect, useRef } from 'react';
import DraggableDialog from 'components/common/DraggableDialog';
import { showAlert } from 'utils/toastBridge';

const POSTCODE_SCRIPT_URL = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

let loaderPromise = null;

/** 카카오(다음) 우편번호 서비스 스크립트 1회 로드 */
function loadDaumPostcode() {
  if (typeof window !== 'undefined' && window.daum?.Postcode) {
    return Promise.resolve(window.daum.Postcode);
  }
  if (!loaderPromise) {
    loaderPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = POSTCODE_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        if (window.daum?.Postcode) resolve(window.daum.Postcode);
        else reject(new Error('우편번호 서비스 초기화 실패'));
      };
      script.onerror = () => {
        loaderPromise = null;
        reject(new Error('우편번호 서비스 스크립트를 불러오지 못했습니다.'));
      };
      document.head.appendChild(script);
    });
  }
  return loaderPromise;
}

/**
 * 카카오 도로명주소 검색 다이얼로그 (공통)
 *
 * @param {boolean} open
 * @param {Function} onSelect - ({ zonecode, roadAddress, jibunAddress, buildingName, address }) => void
 *                              address: "(우편번호) 도로명주소 (건물명)" 조합 문자열
 * @param {Function} onClose
 */
const AddressSearchDialog = ({ open, onSelect, onClose }) => {
  const bodyRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);
  onSelectRef.current = onSelect;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || !bodyRef.current) return;
    let cancelled = false;
    const el = bodyRef.current;

    loadDaumPostcode()
      .then((Postcode) => {
        if (cancelled || !el) return;
        el.innerHTML = '';
        new Postcode({
          width: '100%',
          height: '100%',
          oncomplete: (data) => {
            const road = data.roadAddress || data.address || '';
            const building = data.buildingName ? ` (${data.buildingName})` : '';
            onSelectRef.current?.({
              zonecode: data.zonecode || '',
              roadAddress: road,
              jibunAddress: data.jibunAddress || '',
              buildingName: data.buildingName || '',
              address: `${data.zonecode ? `(${data.zonecode}) ` : ''}${road}${building}`.trim(),
            });
            onCloseRef.current?.();
          },
        }).embed(el);
      })
      .catch((err) => {
        showAlert(err?.message || '주소 검색을 열 수 없습니다.', 'error');
        onCloseRef.current?.();
      });

    return () => {
      cancelled = true;
      el.innerHTML = '';
    };
  }, [open]);

  return (
    <DraggableDialog
      open={open}
      title="도로명주소 검색"
      onClose={onClose}
      dialogWidth="520px"
      dialogHeight="560px"
      keepMounted={false}
    >
      <div ref={bodyRef} style={{ width: '100%', flex: 1, minHeight: 440 }} />
    </DraggableDialog>
  );
};

export default AddressSearchDialog;
